import { useState } from "react";
import { Lightbulb, ArrowRight, BookmarkSimple, Check } from "@phosphor-icons/react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { ByokPanel } from "@/components/ByokPanel";
import { CopyButton } from "@/components/CopyButton";
import { useByok } from "@/hooks/useByok";
import { useSeo } from "@/lib/seo";

const FORMAT_COLORS = {
  Tutorial: "var(--glow)", Listicle: "var(--coral)", Challenge: "#F5A623", Story: "var(--success)",
  Comparison: "#4DA6FF", Documentary: "#C77CFF", Reaction: "#FF7AB8",
};

export default function AiIdeas() {
  useSeo("AI Video Idea Generator", "Brainstorm 6 full YouTube video ideas — title, hook, angle and format — from any niche keyword with your own AI key.");
  const byok = useByok();
  const navigate = useNavigate();
  const [niche, setNiche] = useState("");
  const [ideas, setIdeas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState({});

  const saveIdea = async (idea, i) => {
    try {
      await api.post("/ideas", { title: idea.title, hook: idea.hook || "", angle: idea.angle || "", format: idea.format || "", niche });
      setSaved((s) => ({ ...s, [i]: true }));
      toast.success("Saved to your Idea Board");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const generate = async (e) => {
    e.preventDefault();
    if (!byok.apiKey.trim()) return toast.error("Paste your API key first — it never leaves your browser except for this request.");
    if (!niche.trim()) return toast.error("Type a niche or keyword first.");
    setLoading(true);
    setIdeas(null);
    setSaved({});
    try {
      const res = await api.post("/ai/ideas", { niche, provider: byok.provider, apiKey: byok.apiKey });
      setIdeas(res.data.ideas);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolShell slug="ai-ideas" title="AI Idea Generator" tagline="Drop in a niche keyword and get 6 full video ideas — title, hook, angle and format — powered by your own AI key." hasResults={!!ideas}>
      <ByokPanel {...byok} />
      <form onSubmit={generate} className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <label className="text-sm font-semibold block mb-2">Niche or keyword</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="ideas-niche-input"
            value={niche}
            onChange={(e) => setNiche(e.target.value)}
            placeholder="budget travel, home barista, indie game dev..."
            className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)]"
          />
          <button
            data-testid="ideas-generate-btn"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm hover:bg-[#6a4ce6] transition-colors disabled:opacity-60"
          >
            <Lightbulb size={16} weight={loading ? "fill" : "bold"} className={loading ? "animate-pulse" : ""} />
            {loading ? "Brainstorming..." : "Generate ideas"}
          </button>
        </div>
      </form>

      {ideas && (
        <div className="mt-8 space-y-4 fade-up" data-testid="ai-ideas-list">
          {ideas.map((idea, i) => (
            <div key={`${i}:${idea.title}`} className="surface rounded-[20px] p-6 lift" data-testid={`idea-card-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-bold text-lg leading-snug">{idea.title}</h3>
                <span
                  className="shrink-0 px-3 py-1 rounded-full text-xs font-bold"
                  style={{
                    background: `color-mix(in srgb, ${FORMAT_COLORS[idea.format] || "var(--glow)"} 14%, transparent)`,
                    color: FORMAT_COLORS[idea.format] || "var(--glow)",
                  }}
                >
                  {idea.format}
                </span>
              </div>
              <p className="text-sm mt-3"><span className="text-[var(--coral)] font-semibold">Hook:</span> {idea.hook}</p>
              <p className="text-sm text-[var(--muted)] mt-2">{idea.angle}</p>
              <div className="flex flex-wrap items-center gap-3 mt-5">
                <button
                  data-testid={`save-idea-${i}`}
                  onClick={() => saveIdea(idea, i)}
                  disabled={saved[i]}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    saved[i]
                      ? "bg-[var(--success)]/12 border border-[var(--success)]/30 text-[var(--success)]"
                      : "surface hover:border-[var(--glow)]"
                  }`}
                >
                  {saved[i] ? <Check size={15} weight="bold" /> : <BookmarkSimple size={15} />}
                  {saved[i] ? "Saved" : "Save"}
                </button>
                <CopyButton text={`${idea.title}\n\nHook: ${idea.hook}\n\n${idea.angle}`} label="Copy idea" testId={`copy-idea-${i}`} />
                <button
                  data-testid={`idea-to-script-${i}`}
                  onClick={() => navigate(`/scripts/new?title=${encodeURIComponent(idea.title)}&hook=${encodeURIComponent(idea.hook)}`)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--coral)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                >
                  Start script <ArrowRight size={14} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </ToolShell>
  );
}
